<div class="revenue-projection-grid__container">
  @if (hasFailed()) {
    <div class="revenue-projection-grid__error">
      <p>Couldn't load the revenue projection.</p>
      <dx-button text="Retry" stylingMode="text" (onClick)="retry()"></dx-button>
    </div>
  } @else if (!isLoading() && !hasProjection()) {
    <!-- The API declines to project without enough history or a settled week (early January).
         Say so plainly rather than rendering an empty grid that reads as "zero production". -->
    <div class="revenue-projection-grid__empty">
      <p>No projection available yet.</p>
      <p class="revenue-projection-grid__empty-hint">
        Needs a completed week of settled production and at least three prior years of history.
      </p>
    </div>
  } @else {
    <dx-data-grid
      class="revenue-projection-grid__grid"
      [dataSource]="rows()"
      [showBorders]="true"
      [columnAutoWidth]="true"
      [wordWrapEnabled]="false"
      [hoverStateEnabled]="true"
      [showRowLines]="true"
      [rowAlternationEnabled]="true"
      height="100%"
      (onCellPrepared)="onCellPrepared($event)"
    >
      <dxo-toolbar [multiline]="true">
        <dxi-item location="before">
          <div *dxTemplate>
            <h5 class="revenue-projection-grid__title">{{ title() }}</h5>
          </div>
        </dxi-item>

        <!-- Hovering an option explains it before it is chosen. The measured error rides in
             the list itself, so `runrate` cannot be picked without seeing that it is the
             worst of the three. -->
        <dxi-item location="after" locateInMenu="never">
          <div *dxTemplate>
            <ag-shr-projection-method-select
              [value]="method()"
              [disabled]="isLoading()"
              (valueChange)="onMethodChanged($event)"
            ></ag-shr-projection-method-select>
          </div>
        </dxi-item>

        <dxi-item location="after" name="columnChooserButton"></dxi-item>
      </dxo-toolbar>

      <!-- Driven by beginCustomLoading from the component: the data source is a plain array,
           so the grid has no load of its own to show a panel for. -->
      <dxo-load-panel [enabled]="true"></dxo-load-panel>
      <dxo-scrolling mode="virtual" rowRenderingMode="virtual"></dxo-scrolling>
      <dxo-sorting mode="single"></dxo-sorting>
      <dxo-column-chooser [enabled]="true" mode="select" [allowSearch]="true"></dxo-column-chooser>
      <!-- Column layout is a per-user preference; the widget can be added more than once, so
           the key is fixed to the component rather than to a widget instance. -->
      <dxo-state-storing
        [enabled]="true"
        type="localStorage"
        storageKey="ag-crm.revenue-projection-grid.v1"
      ></dxo-state-storing>

      <!-- Pinned: the money columns overflow a narrow widget, and the row is
           unreadable once the carrier scrolls out of view. -->
      <dxi-column
        dataField="carrier"
        caption="Carrier"
        [allowSorting]="true"
        [fixed]="true"
        fixedPosition="left"
      ></dxi-column>

      <!-- Share rides along with YTD rather than owning a column: it is that number expressed
           as a proportion, and one carrier holds ~87% — a column of its own spent width to
           restate what the YTD ordering already shows. -->
      <dxi-column
        dataField="ytd"
        caption="Settled YTD"
        dataType="number"
        [format]="DX_USD_CURRENCY_FORMAT"
        alignment="right"
        cellTemplate="ytdCellTemplate"
      ></dxi-column>

      <div *dxTemplate="let cell of 'ytdCellTemplate'">
        <span class="revenue-projection-grid__ytd">
          <span>{{ cell.text }}</span>
          <span class="revenue-projection-grid__share">{{ cell.data.sharePct | number: "1.1-1" }}%</span>
        </span>
      </div>

      <dxi-column
        dataField="projectedAnnual"
        caption="Projected year"
        dataType="number"
        [format]="DX_USD_CURRENCY_FORMAT"
        alignment="right"
        [sortIndex]="0"
        sortOrder="desc"
      ></dxi-column>

      <!-- Accuracy rides on the bonus itself rather than in its own column: it qualifies this
           number and nothing else, and it ranges from ~±5% on the largest carrier to ±60% on
           sparse ones. Keeping them apart invites reading the bonus without its error bar. -->
      <dxi-column
        dataField="bonus"
        caption="Bonus"
        dataType="number"
        [format]="DX_USD_CURRENCY_FORMAT"
        alignment="right"
        cssClass="revenue-projection-grid__bonus-cell"
        cellTemplate="bonusCellTemplate"
      ></dxi-column>

      <div *dxTemplate="let cell of 'bonusCellTemplate'">
        <span class="revenue-projection-grid__bonus">
          <span class="revenue-projection-grid__bonus-value">{{ cell.text }}</span>
          <i
            class="dx-icon-info revenue-projection-grid__accuracy-icon"
            [class.revenue-projection-grid__accuracy-icon--high]="cell.data.confidence === 'high'"
            [class.revenue-projection-grid__accuracy-icon--medium]="cell.data.confidence === 'medium'"
            [class.revenue-projection-grid__accuracy-icon--low]="cell.data.confidence === 'low'"
            [class.revenue-projection-grid__accuracy-icon--none]="cell.data.confidence === 'none'"
            [attr.aria-label]="'Accuracy: ' + confidenceLabel(cell.data)"
            (mouseenter)="openAccuracyPopover($event, cell.data)"
            (click)="openAccuracyPopover($event, cell.data)"
          ></i>
        </span>
      </div>

      <!-- Client-side: ~11 rows, so remoteOperations buys nothing. The sum ties to the
           headline total exactly, because rows are allocated top-down from it. -->
      <dxo-summary>
        <dxi-total-item
          column="ytd"
          summaryType="sum"
          displayFormat="&#8721; {0}"
          valueFormat="$#,##0.00"
        ></dxi-total-item>
        <dxi-total-item
          column="projectedAnnual"
          summaryType="sum"
          displayFormat="&#8721; {0}"
          valueFormat="$#,##0.00"
        ></dxi-total-item>
        <dxi-total-item
          column="bonus"
          summaryType="sum"
          displayFormat="&#8721; {0}"
          valueFormat="$#,##0.00"
        ></dxi-total-item>
      </dxo-summary>
    </dx-data-grid>

    <!-- Settled-data note: ytd is cut back from today to clear the carrier reporting lag, so it
         will not tie out against a raw year-to-date figure. Saying which week it stops at is
         what makes the numbers reconcilable. Guarded on projection(): while loading there is no
         as-of week yet, and an "undefined" note under the load panel is worse than none. -->
    @if (projection(); as projection) {
      <p class="revenue-projection-grid__note">
        Loaded through week {{ projection.dataWeek }} of {{ projection.asOfYear }}, projected from week
        {{ projection.asOfWeek }} · bonus at {{ ratePct() }}% · {{ projection.historicalYears }} years of history ·
        the {{ projection.lagWeeks }} most recent weeks are excluded from the estimate (carriers report late)
      </p>

      <!-- Reads off the response, so it appears only once numbers computed with a non-default
           method are actually on screen. Anyone screenshotting this table needs the caveat in
           the frame, not in a dropdown they have to open. -->
      @if (isNonDefaultMethod()) {
        <p class="revenue-projection-grid__method-warning">
          <i class="dx-icon-warning"></i>
          <span>
            Showing <b>{{ computedMethodOption()?.label }}</b> (±{{ computedMethodOption()?.mape | number: "1.1-2" }}%
            average error, worst year {{ computedMethodOption()?.worst | number: "1.1-1" }}%) instead of Credibility
            (±2.25%). For comparison only — not a basis for a payout.
          </span>
        </p>
      }
    }
  }
</div>

<!-- One popover for every icon — per-row and the summary's — retargeted on open. The grid
       renders a cell template per row, so an instance each would be wasteful. Same shape as
       agent-merge-notes. -->
<dx-popover
  [(visible)]="isAccuracyPopoverVisible"
  [target]="accuracyPopoverTarget"
  [hideOnOutsideClick]="true"
  [showEvent]="'mouseenter'"
  [hideEvent]="'mouseleave'"
  [width]="290"
  position="top"
>
  <div *dxTemplate="let data of 'content'">
    <!-- Nested rather than `@else if (... ; as row)`: Angular only allows an `as` alias on
           a primary @if, not on an @else if. -->
    @if (popoverKind === "summary") {
      @if (summary(); as summary) {
        <div class="revenue-projection-grid__popover">
          <div class="revenue-projection-grid__popover-title">
            @if (totalAccuracyPct() !== null) {
              Expected bonus ±{{ totalAccuracyPct() | number: "1.1-1" }}%
            } @else {
              Expected bonus
            }
          </div>

          <p class="revenue-projection-grid__popover-text">
            Projected from settled production only. The total is more accurate than any single carrier — errors partly
            cancel across the mix.
          </p>

          <div class="revenue-projection-grid__popover-range">
            <span>Likely between</span>
            <b>{{ summary.totalBonusLow | currency }} … {{ summary.totalBonusHigh | currency }}</b>
          </div>

          @if (summary.prevYearBonus) {
            <div class="revenue-projection-grid__popover-range">
              <span>vs {{ projection()?.asOfYear - 1 }}</span>
              <b>
                {{ summary.prevYearBonus | currency }}
                @if (vsPrevYearPct() !== null) {
                  <span
                    class="revenue-projection-grid__delta"
                    [class.revenue-projection-grid__delta--down]="vsPrevYearPct()! < 0"
                  >
                    ({{ vsPrevYearPct()! > 0 ? "+" : "" }}{{ vsPrevYearPct()! | number: "1.1-1" }}%)
                  </span>
                }
              </b>
            </div>
          }

          <!-- Cross-check, not a second answer: the headline comes from the total projection;
               this is the same year summed bottom-up per carrier. They should agree closely —
               a wide gap means the carrier mix is behaving unusually and is worth a look. -->
          <div
            class="revenue-projection-grid__popover-range revenue-projection-grid__crosscheck"
            [class.revenue-projection-grid__crosscheck--notable]="isDeltaNotable()"
          >
            <span>Cross-check (summed per carrier)</span>
            <b>
              {{ summary.totalBonusAltSum | currency }} ({{ deltaAbPct() > 0 ? "+" : ""
              }}{{ deltaAbPct() | number: "1.1-1" }}%)
            </b>
            @if (isDeltaNotable()) {
              <span class="revenue-projection-grid__popover-warn">
                Wide gap — the carrier mix is behaving unusually.
              </span>
            }
          </div>
        </div>
      }
    } @else {
      @if (accuracyPopoverRow; as row) {
        <div class="revenue-projection-grid__popover">
          <div class="revenue-projection-grid__popover-title">
            {{ row.errorPct === null ? "No history" : "Accuracy ±" + row.errorPct.toFixed(1) + "%" }}
          </div>

          @if (row.errorPct === null) {
            <p class="revenue-projection-grid__popover-text">
              This carrier has no usable history to measure against, so its bonus is an allocation of the total rather
              than a projection of its own.
            </p>
          } @else {
            <p class="revenue-projection-grid__popover-text">
              How far this carrier's projection has typically landed from the real figure in past years. Measured, not
              assumed.
            </p>
            <div class="revenue-projection-grid__popover-range">
              <span>Likely between</span>
              <b>{{ row.bonusLow | currency }} … {{ row.bonusHigh | currency }}</b>
            </div>
          }
        </div>
      }
    }
  </div>
</dx-popover>
